import type { ChangeEvent } from 'react';

/**
 * THE labeled calendar-date field primitive (FOLLOWUP-DUE-DATE-PLUMBING-V1).
 *
 * Minted because no registered ui/ owner of a native date input existed when
 * the COC follow-up recorder needed one: bare date inputs live only in
 * pre-pin screens, and the workboard surface is PINNED to design-system
 * controls only (coc-followup-workboard.test.tsx forbids any raw input in
 * that page file) -- the conformance guard's own remedy line applies ("add
 * the missing primitive to ui/ instead", design-system-conformance-guard).
 *
 * ONE DATE-INPUT PATTERN (the R-41 date-label family; FOUNDER-WALK-2026-07-31
 * F9, banked at appointments/IntakeDrawer.tsx): a PLAIN label with no
 * format hint -- the native picker renders the BROWSER-locale format, so any
 * parenthetical promise here would lie to someone. Value contract is the
 * wire's own bare YYYY-MM-DD ('' = unset); the native control emits exactly
 * that shape. PRESENTATION-ONLY: nothing fetched, derived, or defaulted --
 * an empty value stays empty until a human picks a day.
 */
export interface DateFieldProps {
  /** The input's DOM id (the label binds to it). */
  readonly id: string;
  /** Plain label text -- never a format hint (R-41). */
  readonly label: string;
  /** Bare YYYY-MM-DD, or '' when unset. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly testId?: string;
  /** Native bounds; enforcement and defaults remain with the caller. */
  readonly min?: string;
  readonly max?: string;
  readonly presentation?: 'default' | 'v6';
}

export default function DateField({
  id,
  label,
  value,
  onChange,
  disabled = false,
  testId,
  min,
  max,
  presentation = 'default',
}: DateFieldProps) {
  return (
    <label className={presentation === 'v6' ? 'ac-datefield ac-datefield--v6' : 'ac-datefield'} htmlFor={id}>
      <span className="ac-datefield__label">{label}</span>
      <input
        id={id}
        type="date"
        value={value}
        disabled={disabled}
        min={min}
        max={max}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        data-testid={testId}
      />
    </label>
  );
}
